import { Component, computed, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { Area, ImageType, JobIssue, Media, SyncableStatus } from "../../../../../core/database/db";
import ScheduleDetailHeader from "../../../../../domains/jobs/components/shared/schedule-detail-header/schedule-detail-header.component";
import { SkeletonModule } from "primeng/skeleton";
import { Bq9DataService } from "../services/bq9-data.service";
import { UiToastService } from "../../../../../shared/components/toast/ui-toast.service";
import { ActivatedRoute, RouterOutlet } from "@angular/router";
import { AssertionImageGalleryComponent } from "../../../../../domains/media/components/assertion-image-gallery/assertion-image-gallery.component";
import { UNASSIGNED_AREA } from "../../../../../domains/areas/models/AreaDTO";
import { ExtendedRouter } from "../../../../../shared/services/extended-router.service";
import { CompleteBottomSheetComponent } from "./components/complete-bottom-sheet/complete-bottom-sheet.component";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { HorizontalButton } from "../../../../../shared/components/horizontal-button-group/horizontal-button-group.component";
import { PhotoCaptureComponent } from "../../../../../shared/components/photo-capture/photo-capture.component";
import { ButtonModule } from "primeng/button";
import { DividerModule } from "primeng/divider";
import { ModalIcon, UiModalService } from "../../../../../shared/components/modal/ui-modal.service";
import { DomSanitizer } from "@angular/platform-browser";
import { Dialog } from "@angular/cdk/dialog";
import { BlobService } from "../../../../../shared/services/blob.service";
import { ImageCompressionPipelineService, MediaForCompression } from "../../../../../domains/media/services/image-compression-pipeline.service";
import { insertAt } from "../../../../../domains/media/utils/media-order.util";
import { ImagesModalComponent } from "../../../../../domains/media/components/images-modal/images-modal.component";
import { BqaImageItem } from "../../../../../domains/media/components/images-modal/bqa-image-item";
import { CONSTANTS } from "../../../../../constants";
import { v4 as uuidv4 } from "uuid";
import { DataUpdatedFadeDirective } from "../../../../../shared/directives/data-updated-fade.directive";

@Component({
  selector: "app-bq9-issue-details",
  imports: [ScheduleDetailHeader, SkeletonModule, AssertionImageGalleryComponent, CompleteBottomSheetComponent, PhotoCaptureComponent, VerticalButtonGroupComponent, ButtonModule, DividerModule, RouterOutlet, DataUpdatedFadeDirective],
  templateUrl: "./bq9-issue-details.component.html",
  styleUrl: "./bq9-issue-details.component.scss"
})
export class Bq9IssueDetailsComponent implements OnInit, OnDestroy {
  readonly maxPhotos = CONSTANTS.CONSTRAINTS.MAX_PHOTOS;

  area: Area = {} as Area;
  issue: JobIssue = {} as JobIssue;
  issueTypeMedia: Media[] = [];
  vendorName: string | undefined;

  images = signal<Media[]>([]);
  allImagesRaw = computed(() => this.images().filter(im => !im.deleted));
  allImages = computed(() =>
    this.allImagesRaw()
      .filter(img => img.base64Image || img.resourceUri)
      .map(img => {
        const azureBlobUri = img.base64Image
          ? this.createObjectUrlAndTrack(this.blobService.base64ToBlob(img.base64Image))
          : img.resourceUri!;
        return this.sanitizer.bypassSecurityTrustUrl(azureBlobUri);
      })
  );
  galleryItems = computed(() =>
    this.allImagesRaw()
      .filter(image => (image.base64Image || image.resourceUri) && !image.deleted)
      .map(image => {
        let azureBlobUri = "";
        if (image.base64Image) {
          azureBlobUri = this.createObjectUrlAndTrack(this.blobService.base64ToBlob(image.base64Image));
        } else if (image.resourceUri) {
          azureBlobUri = image.resourceUri;
        }
        return new BqaImageItem({
          azureBlobUri: azureBlobUri || "",
          mediaIdentifier: image.fileIdentifier,
          isThumbnailOnly: image.isThumbnailOnly || false
        });
      })
  );

  private issueIdentifierForEncoding!: string;
  private objectUrls = new Set<string>();
  private initialImageFileIdentifiers = new Set<string>();

  showDetailsBottomSheet = false;
  isDeleting = false;
  isRefreshingInBackground = computed(() => this.bq9DataService.isRefreshingInBackground());
  dataUpdatedPulse = computed(() => this.bq9DataService.dataUpdatedPulse());

  get isResolved(): boolean {
    return this.issue.isConfirmedByHomeowner || this.issue.postCompleteStatus !== null && this.issue.postCompleteStatus !== undefined;
  }

  constructor(
    private route: ActivatedRoute,
    private bq9DataService: Bq9DataService,
    private toastService: UiToastService,
    private router: ExtendedRouter,
    private modalService: UiModalService,
    private sanitizer: DomSanitizer,
    private blobService: BlobService,
    public imageCompressionPipelineService: ImageCompressionPipelineService,
    private dialog: Dialog = inject(Dialog)
  ) { }

  ngOnDestroy(): void {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
  }

  ngOnInit(): void {
    this.issueIdentifierForEncoding = uuidv4();
    const areaId = parseInt(this.route.snapshot.paramMap.get("areaId") ?? "0");
    const issueId = parseInt(this.route.snapshot.paramMap.get("issueId") ?? "0");

    const areas = [...this.bq9DataService.areas() || [], UNASSIGNED_AREA];
    const issues = this.bq9DataService.jobIssues();
    const media = this.bq9DataService.media();

    const area = areas?.find(a => a.id === areaId);
    const issue = issues?.find(i => i.id === issueId);
    const issueMedia = media?.filter(m => m.parentIdentifier === issue?.identifier) ?? [];

    const hasData = !!area && !!issue && !!issueMedia;
    if (hasData === false) {
      this.toastService.error("Error loading issue data. Please try again.");
      this.goBack();
      return;
    }

    this.area = area;
    this.issue = issue;
    this.issueTypeMedia = issueMedia.filter(m => m.imageType === ImageType.Issue);

    const confirmationRaw = issueMedia.filter(m => m.imageType === ImageType.ConfirmationHomeowner && !m.deleted);
    const confirmationForEditing = confirmationRaw.map(img => ({
      ...img,
      id: undefined,
      parentIdentifier: this.issueIdentifierForEncoding,
      syncStatus: SyncableStatus.Pending,
      createdAt: new Date(),
      updatedAt: new Date()
    }));
    this.images.set(confirmationForEditing);
    this.initialImageFileIdentifiers = new Set(
      confirmationForEditing.map(img => img.fileIdentifier).filter((id): id is string => !!id)
    );

    const vendor = (this.bq9DataService.jobIssueVendors() ?? [])
      .find(v => v.issueIdentifier === issue.identifier);
    this.vendorName = vendor?.vendorName;
  }

  get resolvedFooterButtons(): VerticalButton[] {
    return [
      {
        label: "Save & Return to List",
        severity: "contrast",
        styleClass: "bqa-primary-btn",
        disabled: this.imageCompressionPipelineService.isCompressingImages(),
        action: () => this.onSave(),
        testId: "bq9-issue-details-btn-save-return"
      },
      {
        label: "Delete Photos & Reopen Item",
        severity: "danger",
        outlined: true,
        styleClass: "bqa-danger-btn",
        disabled: this.isDeleting,
        action: () => this.showReopenModal(),
        testId: "bq9-issue-details-btn-reopen"
      },
      {
        label: "Cancel",
        severity: "secondary",
        text: true,
        styleClass: "bqa-secondary-btn",
        action: () => this.showCancelModal(),
        testId: "bq9-issue-details-btn-cancel"
      }
    ];
  }

  get completeQuestionButtons(): HorizontalButton[] {
    return [
      {
        label: "Yes",
        severity: "contrast",
        styleClass: "bqa-primary-btn",
        action: () => (this.showDetailsBottomSheet = true),
        testId: "bq9-issue-details-btn-more-actions"
      },
      {
        label: "No",
        severity: "secondary",
        styleClass: "bqa-secondary-btn",
        action: () => this.goToNotComplete(),
        testId: "bq9-issue-details-btn-not-complete"
      }
    ];
  }

  async savePhotoRequired(images: Media[]): Promise<void> {
    this.showDetailsBottomSheet = false;
    this.bq9DataService.saveCompleteIssue(this.issue, images);
    this.goBack();
  }

  async onSave(): Promise<void> {
    await this.imageCompressionPipelineService.waitForCompletion();
    const mediaToSave: Media[] = this.images().map(image => {
      const { file: _file, ...media } = image as MediaForCompression;
      return media;
    });
    await this.bq9DataService.updateCompleteIssueMedia(this.issue, mediaToSave);
    this.navigateBack();
  }

  async onPhotosSelected(files: File[], insertIndex?: number): Promise<Media[]> {
    if (files.length === 0) {
      return [];
    }
    const mediaRecords = await this.imageCompressionPipelineService.encode(
      files, this.issueIdentifierForEncoding, ImageType.ConfirmationHomeowner
    );
    this.images.update(existing => insertAt(existing, mediaRecords, insertIndex));
    return mediaRecords;
  }

  openImagesModal(index: number): void {
    const dialogRef = this.dialog.open<string>(ImagesModalComponent, {
      data: {
        images: this.galleryItems(),
        index,
        isIssue: true,
        onDeleted: (fileIdentifier: string) => this.handleImageDeleted(fileIdentifier),
        onAnnotated: (fileIdentifier: string, file: File) => this.handleImageAnnotated(fileIdentifier, file),
        backdropClass: "images-modal-backdrop"
      }
    });
    dialogRef.closed.subscribe(_ => { });
  }

  async handleImageAnnotated(originalFileIdentifier: string, annotatedFile: File): Promise<string | undefined> {
    const index = this.images().findIndex(img => img.fileIdentifier === originalFileIdentifier);
    this.handleImageDeleted(originalFileIdentifier);
    const [added] = await this.onPhotosSelected([annotatedFile], index);
    return added?.fileIdentifier;
  }

  handleImageDeleted(fileIdentifier: string): void {
    this.images.update(existing => existing.filter(img => img.fileIdentifier !== fileIdentifier));
  }

  private createObjectUrlAndTrack(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.add(url);
    return url;
  }

  showCancelModal(): void {
    const currentIds = new Set(
      this.images()
        .filter(img => !img.deleted)
        .map(img => img.fileIdentifier)
        .filter((id): id is string => !!id)
    );
    const hasChanges =
      currentIds.size !== this.initialImageFileIdentifiers.size ||
      [...currentIds].some(id => !this.initialImageFileIdentifiers.has(id));

    if (!hasChanges) {
      this.navigateBack();
      return;
    }

    this.modalService.showModal({
      icon: ModalIcon.None,
      header: "Cancel",
      subHeader: "Are you sure you want to proceed?",
      message: "Unsaved changes will be lost.",
      cancelLabel: "No",
      onCancel: () => { },
      actionLabel: "Yes",
      onAction: () => this.navigateBack(),
      isDestructive: true
    });
  }

  showReopenModal(): void {
    this.modalService.showModal({
      icon: ModalIcon.None,
      header: "Reopen Item",
      subHeader: "Are you sure you want to proceed?",
      message: "This will delete your completion photos and reset this item.",
      cancelLabel: "No",
      onCancel: () => { },
      actionLabel: "Yes",
      onAction: () => this.reopenItem(),
      isDestructive: true
    });
  }

  async reopenItem(): Promise<void> {
    this.isDeleting = true;
    try {
      await this.bq9DataService.reopenCompleteIssue(this.issue);
      this.goBack();
    } catch {
      this.toastService.error("Failed to reopen item. Please try again.");
    } finally {
      this.isDeleting = false;
    }
  }

  goBack(): void {
    if (this.issue?.isConfirmedByHomeowner) {
      this.showCancelModal();
      return;
    }
    this.navigateBack();
  }

  private navigateBack(): void {
    this.router.navigateWithPersistedState(["../../../../"], {
      relativeTo: this.route
    });
  }

  goToNotComplete(): void {
    this.router.navigateWithPersistedState(["not-complete"], { relativeTo: this.route });
  }
}
